import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowRight, Lock, AlertCircle } from 'lucide-react';
import { RippleButton } from '../components/effects/RippleButton';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientLogin() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API}/api/client/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Store session
      localStorage.setItem('clientToken', data.token);
      localStorage.setItem('clientName', data.client_name);
      localStorage.setItem('folderName', data.folder_name);

      // Redirect to portal
      navigate('/client/portal');
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-cream flex items-center justify-center px-6" data-testid="client-login-page">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl shadow-lg p-8 md:p-10 border border-border/30"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-forest" />
            </div>
            <h1 className="font-serif text-2xl md:text-3xl text-forest mb-2">
              Client Portal
            </h1>
            <p className="text-foreground/60 text-sm">
              Access your project files and documents
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground/70 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full pl-12 pr-4 py-3 bg-sand/30 border border-border/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest/50 transition-all"
                  data-testid="client-email-input"
                />
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}

            <RippleButton
              type="submit"
              disabled={isLoading || !email}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="client-login-btn"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Access Portal
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </RippleButton>
          </form>

          {/* Help Text */}
          <p className="text-center text-foreground/50 text-xs mt-6">
            Don't have access? Contact us at{' '}
            <a href="mailto:reachout@feralways.org" className="text-forest hover:underline">
              reachout@feralways.org
            </a>
          </p>
        </motion.div>
      </div>
    </main>
  );
}
